import type {
  AoSGameState,
  HexCoordinate,
  CompletedLink,
  PlayerId,
  Direction,
  Hex,
  PathSegment
} from '../../types';
import { coordToKey, getAdjacentHex, getOppositeDirection } from '../../types';
import { debug } from '../../utils/debug';

/**
 * connections を正規化して Direction[] に変換
 *
 * connections は2つの形式が混在している:
 * - 旧形式: Direction[] (例: [0, 3])
 * - 新形式: number[][] (例: [[0, 3]] または [[0, 3], [1, 5]])
 *
 * この関数は両方の形式を Direction[] に正規化する
 *
 * @param connections - タイルの connections
 * @returns Direction[] 形式に正規化された接続方向
 */
function normalizeConnections(connections: Direction[] | number[][] | undefined): Direction[] {
  if (!connections || connections.length === 0) {
    return [];
  }

  // 最初の要素が配列かどうかで形式を判定
  const firstElement = connections[0];
  if (Array.isArray(firstElement)) {
    // 新形式: [[0, 3], [1, 5]] -> [0, 3, 1, 5]
    const nestedConnections = connections as number[][];
    const flattened: Direction[] = [];
    for (const pair of nestedConnections) {
      for (const dir of pair) {
        if (!flattened.includes(dir as Direction)) {
          flattened.push(dir as Direction);
        }
      }
    }
    return flattened;
  } else {
    // 旧形式: [0, 3] -> [0, 3]
    return connections as Direction[];
  }
}

/**
 * connections からペア情報を取得
 *
 * @param connections - タイルの connections
 * @returns [Direction, Direction][] 形式のペア配列
 */
function getConnectionPairs(connections: Direction[] | number[][] | undefined): [Direction, Direction][] {
  if (!connections || connections.length === 0) {
    return [];
  }

  // 最初の要素が配列かどうかで形式を判定
  const firstElement = connections[0];
  if (Array.isArray(firstElement)) {
    // 新形式: [[0, 3], [1, 5]] -> [[0, 3], [1, 5]]
    return (connections as number[][]).map(pair => [pair[0] as Direction, pair[1] as Direction]);
  } else {
    // 旧形式: [0, 3] -> [[0, 3]] (単線として扱う)
    const dirs = connections as Direction[];
    if (dirs.length === 2) {
      return [[dirs[0], dirs[1]]];
    }
    // 3方向以上の場合はペア情報が不明なので空配列
    return [];
  }
}

/**
 * entryEdge に対応する exitEdge を取得
 *
 * @param connectionPairs - [Direction, Direction][] 形式のペア配列
 * @param entryEdge - 入ってきた Edge
 * @returns 出ていく Edge、またはnull
 */
function findExitEdge(
  connectionPairs: [Direction, Direction][],
  entryEdge: Direction
): Direction | null {
  for (const [edge1, edge2] of connectionPairs) {
    if (edge1 === entryEdge) return edge2;
    if (edge2 === entryEdge) return edge1;
  }
  return null;
}

/**
 * タイル配置後、新たに完成したリンクを検出する
 *
 * Issue #12 修正: Town ヘクス上にタイルを配置した場合、
 * 各方向への探索結果と Town を組み合わせてリンクを作成する。
 * 複数のリンクが完成する可能性があるため、配列を返す。
 *
 * @param G - ゲーム状態
 * @param placedCoord - 配置したタイルの座標
 * @param playerId - プレイヤーID
 * @returns 完成したリンクの配列（後方互換性のため、最初の1つをnullableで返す）
 */
export function detectNewCompletedLink(
  G: AoSGameState,
  placedCoord: HexCoordinate,
  playerId: PlayerId
): CompletedLink | null {
  const links = detectNewCompletedLinks(G, placedCoord, playerId);
  return links.length > 0 ? links[0] : null;
}

/**
 * タイル配置後、新たに完成したリンクをすべて検出する
 *
 * Issue #12 修正: Town ヘクス上にタイルを配置した場合、
 * 各方向への探索結果と Town を組み合わせてリンクを作成する。
 *
 * @param G - ゲーム状態
 * @param placedCoord - 配置したタイルの座標
 * @param playerId - プレイヤーID
 * @returns 完成したリンクの配列
 */
export function detectNewCompletedLinks(
  G: AoSGameState,
  placedCoord: HexCoordinate,
  playerId: PlayerId
): CompletedLink[] {
  const placedHex = getHex(G, placedCoord);
  if (!placedHex || !placedHex.tile) {
    return [];
  }

  const connections = placedHex.tile.connections;
  if (!connections) {
    return []; // connectionsが未定義の場合
  }

  // Issue #12: placedCoord が Town ヘクスの場合、Town を起点として各方向へリンクを検出
  if (placedHex.town) {
    const links: CompletedLink[] = [];
    const townId = getCityOrTownId(G, placedCoord);
    // 重複リンク防止（同じパスのリンクを防ぐ - 同じ端点でも異なるルートは別リンク）
    const seenPaths = new Set<string>();

    // 既存のリンクも重複チェック対象に追加（パスベース）
    for (const existingLink of G.completedLinks) {
      const pathKey = existingLink.path.map(c => `${c.q},${c.r}`).join('|');
      const reversePathKey = [...existingLink.path].reverse().map(c => `${c.q},${c.r}`).join('|');
      seenPaths.add(pathKey);
      seenPaths.add(reversePathKey);
    }

    // Town タイルは中心から各方向への接続なので、normalizeConnections を使用
    const dirs = normalizeConnections(connections);

    for (const dir of dirs) {
      const endpoint = traceToEndpoint(G, placedCoord, dir, playerId);
      if (endpoint && (endpoint.type === 'city' || endpoint.type === 'town')) {
        const endpointId = getCityOrTownId(G, endpoint.coord);

        // Issue #29: 同一都市/町へのリンク（自己参照）は作成しない
        if (townId === endpointId) {
          console.warn(`[detectNewCompletedLinks] Skipping self-referencing town link: ${townId}`);
          continue;
        }

        // Town → endpoint のリンクを作成（重複チェック前にパスを計算）
        const pathSegments = reconstructPath(G, placedCoord, placedCoord, endpoint.coord);
        const path = pathSegments.map(s => s.coord);

        // パスベースの重複チェック（同じルートのリンクのみスキップ）
        const pathKey = path.map(c => `${c.q},${c.r}`).join('|');
        const reversePathKey = [...path].reverse().map(c => `${c.q},${c.r}`).join('|');

        if (seenPaths.has(pathKey) || seenPaths.has(reversePathKey)) {
          debug.log(`[detectNewCompletedLinks] Skipping duplicate path: ${townId} → ${endpointId}`);
          continue;
        }
        seenPaths.add(pathKey);
        seenPaths.add(reversePathKey);

        const linkCount = calculateLinkCount(G, path);

        const link: CompletedLink = {
          id: generateLinkId(),
          startId: townId,
          endId: endpointId,
          path: path,
          pathSegments: pathSegments,
          owner: playerId,
          linkCount: linkCount,
          completedTurn: 1 // TODO: G.currentTurn から取得
        };

        links.push(link);
        debug.log(`[detectNewCompletedLinks] Town link detected: ${link.startId} → ${link.endId}`);
      }
    }

    return links;
  }

  // 通常のタイル配置の場合
  // Issue: クロス線路のペア情報を維持して、各ペアを個別に処理
  const connectionPairs = getConnectionPairs(connections);
  const links: CompletedLink[] = [];

  // 重複チェック用のセット（既存リンク + 今回作成したリンク）
  // 端点だけでなくパスも含めてチェック（同じ端点でも異なるルートは別リンク）
  const seenPaths = new Set<string>();
  for (const existingLink of G.completedLinks) {
    // パスの座標列を正規化してキーを生成
    const pathKey = existingLink.path.map(c => `${c.q},${c.r}`).join('|');
    const reversePathKey = [...existingLink.path].reverse().map(c => `${c.q},${c.r}`).join('|');
    seenPaths.add(pathKey);
    seenPaths.add(reversePathKey);
  }

  // 各ペアを個別に処理（クロス線路では複数ペアが存在）
  for (const [dir1, dir2] of connectionPairs) {
    const endpoint1 = traceToEndpoint(G, placedCoord, dir1, playerId);
    const endpoint2 = traceToEndpoint(G, placedCoord, dir2, playerId);

    // 両端が都市/町の場合、リンク完成
    if (
      endpoint1 && endpoint2 &&
      (endpoint1.type === 'city' || endpoint1.type === 'town') &&
      (endpoint2.type === 'city' || endpoint2.type === 'town')
    ) {
      const startId = getCityOrTownId(G, endpoint1.coord);
      const endId = getCityOrTownId(G, endpoint2.coord);

      // Issue #29: 同一都市/町へのリンク（自己参照）は作成しない
      if (startId === endId) {
        console.warn(`[detectNewCompletedLinks] Skipping self-referencing link: ${startId}`);
        continue;
      }

      // リンクを作成（重複チェック前にパスを計算）
      const pathSegments = reconstructPath(G, endpoint1.coord, placedCoord, endpoint2.coord);
      const path = pathSegments.map(s => s.coord);

      // パスベースの重複チェック（同じルートのリンクのみスキップ）
      const pathKey = path.map(c => `${c.q},${c.r}`).join('|');
      const reversePathKey = [...path].reverse().map(c => `${c.q},${c.r}`).join('|');

      if (seenPaths.has(pathKey) || seenPaths.has(reversePathKey)) {
        debug.log(`[detectNewCompletedLinks] Skipping duplicate path: ${startId} → ${endId}`);
        continue;
      }
      seenPaths.add(pathKey);
      seenPaths.add(reversePathKey);

      const linkCount = calculateLinkCount(G, path);

      const link: CompletedLink = {
        id: generateLinkId(),
        startId: startId,
        endId: endId,
        path: path,
        pathSegments: pathSegments,
        owner: playerId,
        linkCount: linkCount,
        completedTurn: 1 // TODO: G.currentTurn から取得
      };

      links.push(link);
      debug.log(`[detectNewCompletedLinks] Link detected: ${link.startId} → ${link.endId}`);
    }
  }

  return links;
}

/**
 * 配置したタイルから指定方向に探索し、都市/町/他プレイヤー線路に到達するかチェック
 *
 * @param G - ゲーム状態
 * @param startCoord - 開始座標（配置したタイルの座標）
 * @param startDir - 開始方向
 * @param playerId - プレイヤーID
 * @returns 到達した終点、またはnull
 */
function traceToEndpoint(
  G: AoSGameState,
  startCoord: HexCoordinate,
  startDir: Direction,
  _playerId: PlayerId
): { coord: HexCoordinate; type: 'city' | 'town' } | null {
  const visited = new Set<string>();
  const queue: Array<{ coord: HexCoordinate; dir: Direction }> = [];

  // 初期状態: startCoordからstartDir方向の隣接Hexへ
  const firstNeighbor = getAdjacentHex(startCoord, startDir);
  queue.push({ coord: firstNeighbor, dir: startDir });
  visited.add(coordToKey(startCoord));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentKey = coordToKey(current.coord);

    // 訪問済みチェック
    if (visited.has(currentKey)) {
      continue;
    }
    visited.add(currentKey);

    const currentHex = getHex(G, current.coord);

    // Hexが存在しない場合は終了
    if (!currentHex) {
      continue;
    }

    // 終点判定
    if (currentHex.city) {
      return { coord: current.coord, type: 'city' };
    }
    if (currentHex.town) {
      // Issue #18 修正: Town は以下の条件を満たす場合のみ終点となる
      // 1. タイルが配置されている（線路が Town まで到達している）
      // 2. 都市化されている（isUrbanized === true && newCity が存在）
      // 3. タイルが探索方向から入れる接続を持っている
      const entryEdge = getOppositeDirection(current.dir);

      if (currentHex.town.isUrbanized && currentHex.town.newCity) {
        // 都市化された Town は City と同様に終点となる
        return { coord: current.coord, type: 'town' };
      }

      if (currentHex.tile) {
        // タイルがある場合、探索方向から入れる接続を持っているかチェック
        const townTileDirs = normalizeConnections(currentHex.tile.connections);
        if (townTileDirs.includes(entryEdge)) {
          return { coord: current.coord, type: 'town' };
        }
        // 接続がない場合は終点にならない
      }
      // 何も配置されていない Town は終点とならない - 探索を継続しない
      continue;
    }

    // タイルがない場合は終了
    if (!currentHex.tile) {
      continue;
    }

    // タイルがあれば所有者に関係なく探索（AoS標準: 全線路通行可能）
    if (currentHex.tile.connections) {
      // Edge ベースの探索: entryEdge から exitEdge を特定
      const entryEdge = getOppositeDirection(current.dir);
      const connectionPairs = getConnectionPairs(currentHex.tile.connections);

      // entryEdge に対応する exitEdge を取得
      const exitEdge = findExitEdge(connectionPairs, entryEdge);

      if (exitEdge !== null) {
        const nextCoord = getAdjacentHex(current.coord, exitEdge);
        const nextKey = coordToKey(nextCoord);

        if (!visited.has(nextKey)) {
          queue.push({ coord: nextCoord, dir: exitEdge });
        }
      }
    }
  }

  // 終点に到達しなかった
  return null;
}

/**
 * 経路を再構築（BFS）- placedCoord を必ず経由するバージョン
 *
 * 修正: placedCoord を経由しない経路が見つかるバグを修正
 * start → placedCoord と placedCoord → end の2段階で経路を構築する
 *
 * @param G - ゲーム状態
 * @param start - 開始座標（都市/町）
 * @param placedCoord - 配置したタイル座標（必ず経由する）
 * @param end - 終了座標（都市/町）
 * @returns PathSegment[] - 経路詳細（座標、タイル種別、使用Edge）
 */
function reconstructPath(
  G: AoSGameState,
  start: HexCoordinate,
  placedCoord: HexCoordinate,
  end: HexCoordinate
): PathSegment[] {
  // start → placedCoord の経路を構築
  const pathToPlaced = reconstructPathSegment(G, start, placedCoord);
  if (pathToPlaced.length === 0) {
    console.error('[reconstructPath] Path not found: start → placedCoord', coordToKey(start), '→', coordToKey(placedCoord));
    return [{ coord: start }, { coord: placedCoord }, { coord: end }];
  }

  // placedCoord → end の経路を構築
  const pathFromPlaced = reconstructPathSegment(G, placedCoord, end);
  if (pathFromPlaced.length === 0) {
    console.error('[reconstructPath] Path not found: placedCoord → end', coordToKey(placedCoord), '→', coordToKey(end));
    return [{ coord: start }, { coord: placedCoord }, { coord: end }];
  }

  // 2つの経路を結合（placedCoord の重複を除く）
  // pathToPlaced: [start, ..., placedCoord]
  // pathFromPlaced: [placedCoord, ..., end]
  // 結合: [start, ..., placedCoord, ..., end] (placedCoord は1回のみ)

  // pathFromPlaced の最初の要素の edgePair を pathToPlaced の最後の要素にマージ
  // （開始点の Town/City から出る方向の edgePair を保持するため）
  const lastIdx = pathToPlaced.length - 1;
  const firstFromPlaced = pathFromPlaced[0];
  if (firstFromPlaced?.edgePair && !pathToPlaced[lastIdx]?.edgePair) {
    pathToPlaced[lastIdx] = {
      ...pathToPlaced[lastIdx],
      edgePair: firstFromPlaced.edgePair
    };
  }

  return [...pathToPlaced, ...pathFromPlaced.slice(1)];
}

/**
 * 2点間の経路を再構築（BFS）- Edge 接続検証版
 *
 * @param G - ゲーム状態
 * @param start - 開始座標
 * @param end - 終了座標
 * @returns PathSegment[] - 経路詳細
 */
function reconstructPathSegment(
  G: AoSGameState,
  start: HexCoordinate,
  end: HexCoordinate
): PathSegment[] {
  // start と end が同じ場合
  if (coordToKey(start) === coordToKey(end)) {
    const hex = getHex(G, start);
    return [{ coord: start, tileType: hex?.tile?.type }];
  }

  interface QueueItem {
    coord: HexCoordinate;
    segments: PathSegment[];
    entryEdge: Direction | null;
    /** 開始点の exitEdge（Town ヘクスの edgePair 設定用） */
    startExitEdge?: Direction;
  }
  const queue: QueueItem[] = [];
  const visited = new Set<string>();

  const getVisitKey = (coord: HexCoordinate, entryEdge: Direction | null): string => {
    return `${coordToKey(coord)}:${entryEdge ?? 'start'}`;
  };

  // 開始点のセグメントを作成
  const startHex = getHex(G, start);
  const startSegment: PathSegment = {
    coord: start,
    tileType: startHex?.tile?.type,
    edgePair: undefined // 後で exitEdge が確定したら設定
  };

  queue.push({ coord: start, segments: [startSegment], entryEdge: null });
  visited.add(getVisitKey(start, null));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentHex = getHex(G, current.coord);

    if (!currentHex) continue;

    // 終点に到達
    if (coordToKey(current.coord) === coordToKey(end)) {
      // 終点が Town/City ヘクスの場合、edgePair を設定（タイルの有無に関わらず）
      // -1 は「中央」を表す特別な値
      if ((currentHex.town || currentHex.city) && current.entryEdge !== null) {
        const lastIdx = current.segments.length - 1;
        current.segments[lastIdx] = {
          ...current.segments[lastIdx],
          edgePair: [current.entryEdge, -1 as number]
        };
      }
      return current.segments;
    }

    // 中間の都市/町に到達した場合、そこから先への探索を行わない
    // （start 以外の都市/町で探索を終了）
    const isStartCoord = coordToKey(current.coord) === coordToKey(start);
    if (!isStartCoord && (currentHex.city || currentHex.town)) {
      // 中間の都市/町なので、ここで探索終了（この経路は無効）
      continue;
    }

    // 出られる方向を取得
    let exitDirections: Direction[];

    if (currentHex.city || currentHex.town) {
      // start 地点の都市/町からは全方向に探索開始
      exitDirections = [0, 1, 2, 3, 4, 5] as Direction[];
    } else if (currentHex.tile) {
      const connectionPairs = getConnectionPairs(currentHex.tile.connections);

      if (current.entryEdge === null) {
        exitDirections = normalizeConnections(currentHex.tile.connections);
      } else {
        const exitEdge = findExitEdge(connectionPairs, current.entryEdge);
        exitDirections = exitEdge !== null ? [exitEdge] : [];
      }
    } else {
      continue;
    }

    for (const exitDir of exitDirections) {
      const nextCoord = getAdjacentHex(current.coord, exitDir);
      const nextHex = getHex(G, nextCoord);
      if (!nextHex) continue;

      const nextEntryEdge = getOppositeDirection(exitDir);

      const visitKey = getVisitKey(nextCoord, nextEntryEdge);
      if (visited.has(visitKey)) continue;

      // Edge 接続検証
      let canEnter = false;

      if (nextHex.city) {
        // City は常に接続可能（終端点）
        canEnter = true;
      } else if (nextHex.town) {
        // Town の場合: 都市化されているか、タイルが正しい方向に接続を持っているかチェック
        if (nextHex.town.isUrbanized && nextHex.town.newCity) {
          // 都市化された Town は City と同様に終端点
          canEnter = true;
        } else if (nextHex.tile) {
          // タイルがある場合、その方向に接続を持っているかチェック
          const nextDirs = normalizeConnections(nextHex.tile.connections);
          canEnter = nextDirs.includes(nextEntryEdge);
        }
      } else if (nextHex.tile) {
        const nextDirs = normalizeConnections(nextHex.tile.connections);
        canEnter = nextDirs.includes(nextEntryEdge);
      }

      if (canEnter) {
        visited.add(visitKey);

        // 現在のセグメントのedgePairを更新
        const newSegments = [...current.segments];
        const lastIdx = newSegments.length - 1;
        if (currentHex.tile && current.entryEdge !== null) {
          newSegments[lastIdx] = {
            ...newSegments[lastIdx],
            edgePair: [current.entryEdge, exitDir]
          };
        } else if (isStartCoord && (currentHex.town || currentHex.city)) {
          // 開始点が Town/City ヘクスの場合（タイルの有無に関わらず）
          // edgePair を [-1, exitDir] として設定（-1 は中央を表す）
          newSegments[0] = {
            ...newSegments[0],
            edgePair: [-1 as number, exitDir]
          };
        }

        // 次のヘクスのセグメントを作成
        let nextEdgePair: [number, number] | undefined;
        if (nextHex.tile) {
          const connectionPairs = getConnectionPairs(nextHex.tile.connections);
          const nextExitEdge = findExitEdge(connectionPairs, nextEntryEdge);
          if (nextExitEdge !== null) {
            nextEdgePair = [nextEntryEdge, nextExitEdge];
          }
        }

        const nextSegment: PathSegment = {
          coord: nextCoord,
          tileType: nextHex.tile?.type,
          edgePair: nextEdgePair
        };

        queue.push({
          coord: nextCoord,
          segments: [...newSegments, nextSegment],
          entryEdge: nextEntryEdge
        });
      }
    }
  }

  // 経路が見つからない場合
  return [];
}

/**
 * リンク数を計算
 * リンク = 都市/町間の区間数（都市A→町→都市B = 2リンク）
 *
 * @param G - ゲーム状態
 * @param path - 経路（Hex座標のリスト）
 * @returns リンク数
 */
function calculateLinkCount(
  G: AoSGameState,
  path: HexCoordinate[]
): number {
  // 経路上の都市/町の数を数える
  let cityTownCount = 0;

  for (const coord of path) {
    const hex = getHex(G, coord);
    if (hex && (hex.city || hex.town)) {
      cityTownCount++;
    }
  }

  // リンク数 = 都市/町の数 - 1
  // 例: 都市A → 都市B = 2個の都市 → 1リンク
  //     都市A → 町 → 都市B = 3個 → 2リンク
  return Math.max(1, cityTownCount - 1);
}

/**
 * 都市または町のIDを取得
 * 新都市タイルが配置されている場合はその IDを返す (Issue #9 修正)
 * @param G - ゲーム状態
 * @param coord - 座標
 * @returns 都市または町のID
 */
function getCityOrTownId(G: AoSGameState, coord: HexCoordinate): string {
  const hex = getHex(G, coord);
  if (!hex) {
    return 'unknown';
  }

  if (hex.city) {
    return hex.city.id;
  }

  if (hex.town) {
    // 新都市タイルが配置されている場合はその IDを返す
    if (hex.town.isUrbanized && hex.town.newCity) {
      return hex.town.newCity.id;
    }
    return hex.town.id;
  }

  return 'unknown';
}

/**
 * Hexを取得
 * @param G - ゲーム状態
 * @param coord - 座標
 * @returns Hex、または undefined
 */
function getHex(G: AoSGameState, coord: HexCoordinate): Hex | undefined {
  const key = coordToKey(coord);
  return G.map.find(h => coordToKey(h.coordinate) === key);
}

/**
 * リンクID生成
 * @returns リンクID
 */
function generateLinkId(): string {
  return `link-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Issue #29: 仮配置状態で self-referencing link が形成されるかをチェック
 *
 * タイルを配置する前に、そのタイルを配置することで同一の都市/町を
 * 両端とするリンクが形成されるかをチェックする。
 *
 * @param G - ゲーム状態
 * @param coord - 仮配置するタイルの座標
 * @param connections - 仮配置タイルの接続情報
 * @param playerId - プレイヤーID
 * @returns self-referencing link が形成される場合は { wouldForm: true, cityId: string }
 */
export function checkWouldFormSelfReferencingLink(
  G: AoSGameState,
  coord: HexCoordinate,
  connections: number[][] | Direction[],
  playerId: PlayerId
): { wouldForm: boolean; cityId?: string } {
  const connectionPairs = getConnectionPairs(connections);

  for (const [dir1, dir2] of connectionPairs) {
    // 各方向の終点を探索（仮配置タイルからの探索）
    const endpoint1 = traceFromHypotheticalTile(G, coord, dir1, playerId);
    const endpoint2 = traceFromHypotheticalTile(G, coord, dir2, playerId);

    // 両端が都市/町の場合
    if (
      endpoint1 && endpoint2 &&
      (endpoint1.type === 'city' || endpoint1.type === 'town') &&
      (endpoint2.type === 'city' || endpoint2.type === 'town')
    ) {
      const id1 = getCityOrTownId(G, endpoint1.coord);
      const id2 = getCityOrTownId(G, endpoint2.coord);

      if (id1 === id2) {
        return { wouldForm: true, cityId: id1 };
      }
    }
  }

  return { wouldForm: false };
}

/**
 * 仮配置タイルから指定方向に探索し、終点を見つける
 *
 * @param G - ゲーム状態
 * @param hypotheticalCoord - 仮配置タイルの座標
 * @param dir - 探索方向
 * @param playerId - プレイヤーID
 * @returns 終点情報、または null
 */
function traceFromHypotheticalTile(
  G: AoSGameState,
  hypotheticalCoord: HexCoordinate,
  dir: Direction,
  _playerId: PlayerId
): { coord: HexCoordinate; type: 'city' | 'town' } | null {
  const visited = new Set<string>();
  visited.add(coordToKey(hypotheticalCoord)); // 仮配置タイル座標を訪問済みに

  // 仮配置タイルの隣接ヘクスから探索開始
  let currentCoord = getAdjacentHex(hypotheticalCoord, dir);
  let currentDir = dir;

  while (true) {
    const currentKey = coordToKey(currentCoord);

    // 訪問済みチェック（ループ防止）
    if (visited.has(currentKey)) {
      return null;
    }
    visited.add(currentKey);

    const currentHex = getHex(G, currentCoord);

    // マップ外
    if (!currentHex) {
      return null;
    }

    // 終点判定: 都市
    if (currentHex.city) {
      return { coord: currentCoord, type: 'city' };
    }

    // 終点判定: 町
    if (currentHex.town) {
      if (currentHex.tile || (currentHex.town.isUrbanized && currentHex.town.newCity)) {
        return { coord: currentCoord, type: 'town' };
      }
      // タイルのない Town は終点にならない
      return null;
    }

    // タイルがない場合
    if (!currentHex.tile) {
      return null;
    }

    // タイルがあれば所有者に関係なく探索（AoS標準: 全線路通行可能）
    if (currentHex.tile.connections) {
      const entryEdge = getOppositeDirection(currentDir);
      const neighborConnections = getConnectionPairs(currentHex.tile.connections);
      const exitEdge = findExitEdge(neighborConnections, entryEdge);

      if (exitEdge === null) {
        return null; // 接続がない
      }

      // 次のヘクスへ
      currentCoord = getAdjacentHex(currentCoord, exitEdge);
      currentDir = exitEdge;
    } else {
      return null;
    }
  }
}

/**
 * ゲーム開始時に敷設済み線路(owner:'')だけで形成される完成リンクを検出
 *
 * マップ上の owner='' タイルを起点に detectNewCompletedLinks を呼び、
 * 重複を除去して返す。
 *
 * @param G - ゲーム状態
 * @returns 敷設済み完成リンクの配列
 */
export function detectPreLaidCompletedLinks(G: AoSGameState): CompletedLink[] {
  const allLinks: CompletedLink[] = [];
  const seenPaths = new Set<string>();

  // owner='' のタイルを持つヘクスを起点に探索
  for (const hex of G.map) {
    if (hex.tile && hex.tile.owner === '') {
      const links = detectNewCompletedLinks(G, hex.coordinate, '');
      for (const link of links) {
        // パスベースの重複チェック
        const pathKey = link.path.map(c => `${c.q},${c.r}`).join('|');
        const reversePathKey = [...link.path].reverse().map(c => `${c.q},${c.r}`).join('|');

        if (!seenPaths.has(pathKey) && !seenPaths.has(reversePathKey)) {
          seenPaths.add(pathKey);
          seenPaths.add(reversePathKey);
          allLinks.push(link);
        }
      }
    }
  }

  return allLinks;
}
